import AppContext from "@common/contexts/App";
import { ModalContext } from "@common/contexts/Modal";
import { type ModalState, getState, saveState } from "@common/controls/Modal/lib";
import Portal from "@common/controls/Portal/component";
import Draggable, { type DraggableHOCComponentProps } from "@common/hocs/draggable";
import { AllowOffScreen, type Boundaries, type LeftTop } from "@common/hocs/draggable/lib";
import { useClickOutside } from "@common/lib/hooks";
import { uuid } from "@common/lib/misc";
import classnames from "classnames";
import * as React from "react";
import { useResizeDetector } from "react-resize-detector";
import Arrow from "./Arrow";
import Body, { type BodyProps } from "./Body/component";
import Content, { type ContentChildren } from "./Content/component";
import Footer from "./Footer/component";
import { type ModalHeaderButtonProps } from "./Header/Buttons";
import Header, { type HeaderProps } from "./Header/component";

import "./style.less";

export type ContentWrapProps = {
  action?: ModalHeaderButtonProps;
  boundaries?: Boundaries /* limits drag boundaries */;
  cannotClose?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  height?: number | string;
  maxHeight?: number | string;
  shouldRenderWhileLoading: boolean;
  isEnlarged?: boolean;
  isPinned?: boolean;
  onBack?: () => void;
  onClose?: () => void;
  onToggleEnlarged?: () => void;
  onTogglePinned?: () => void;
  ref?: React.RefObject<HTMLDivElement | null>;
  children: any;
};
const ContentWrap = ({
  action,
  isEnlarged,
  isPinned,
  onBack,
  onClose,
  onToggleEnlarged,
  onTogglePinned,
  ...props
}: ContentWrapProps) => {
  const convertChildren = (): ContentChildren => {
    const children = React.Children.toArray(props.children);

    const header =
      (children.find(
        (ch) => (ch as React.ReactElement<any>).type === Modal.Header
      ) as React.ReactElement<HeaderProps>) || null;
    const body =
      (children.find((ch) => (ch as React.ReactElement<any>).type === Modal.Body) as React.ReactElement<BodyProps>) ||
      null;
    const footer = children.find((ch) => (ch as React.ReactElement<any>).type === Modal.Footer) || null;

    return {
      header: wrapHeader(header),
      body,
      footer
    };
  };

  const wrapHeader = (header: React.ReactElement<HeaderProps> | null): React.ReactElement<HeaderProps> | null => {
    if (!header) return null;

    return React.cloneElement(header, {
      action,
      isEnlarged,
      isPinned,
      onBackClick: onBack,
      onCloseClick: onClose,
      onToggleEnlarged: onToggleEnlarged,
      onTogglePinned: onTogglePinned
    });
  };

  return <Content {...props}>{convertChildren()}</Content>;
};

type DialogProps = ContentWrapProps & {
  className: string;
  hasArrow?: boolean;
  style?: React.CSSProperties;
};
const Dialog = (props: DialogProps & DraggableHOCComponentProps<HTMLDivElement>) => {
  const {
    action,
    cannotClose,
    boundaries,
    hasArrow,
    height,
    isEnlarged,
    isLoading,
    isPinned,
    children,
    maxHeight,
    pin,
    onClose,
    onBack,
    onResized,
    onToggleEnlarged,
    onTogglePinned,
    shouldRenderWhileLoading,
    ref,
    unpin,
    ...baseProps
  } = props;

  const { ref: resizeRef } = useResizeDetector<HTMLDivElement>({
    handleHeight: true,
    handleWidth: true,
    onResize: onResized ? (args) => onResized(args.width, args.height) : undefined
  });

  return (
    <div ref={ref} {...baseProps}>
      {!!hasArrow && <Arrow />}

      <ContentWrap
        ref={resizeRef as React.RefObject<HTMLDivElement | null>}
        action={action}
        cannotClose={cannotClose}
        children={children}
        height={height}
        maxHeight={maxHeight}
        isEnlarged={isEnlarged}
        isLoading={isLoading}
        isPinned={isPinned}
        onBack={onBack}
        onClose={onClose}
        onToggleEnlarged={onToggleEnlarged}
        onTogglePinned={onTogglePinned}
        shouldRenderWhileLoading
      />
    </div>
  );
};

const EnhancedDialog = Draggable<HTMLDivElement, DialogProps>(Dialog, ".modal-header", [".modal-header-btn"]);

export type Position = {
  left: number;
  top: number;
  position: string;
};
export type ModalInnerProps = {
  action?: ModalHeaderButtonProps;
  boundaries?: Boundaries /* limits drag boundaries */;
  cannotClose?: boolean;
  children: any;
  className?: string;
  defaultIsPinned?: boolean;
  defaultIsEnlarged?: boolean;
  enlagedHeight?: number;
  enlagedWidth?: number;
  errorMessage?: string;
  hasArrow?: boolean;
  hasBackdrop?: boolean;
  height?: string | number;
  isBorderless?: boolean;
  isCentered?: boolean;
  isDraggable?: boolean;
  isLoading?: boolean;
  isNarrow?: boolean;
  isTopmost?: boolean;
  loadingText?: string;
  maxHeight?: string | number;
  maxWidth?: string | number;
  onBack?: () => void;
  onClose: () => void;
  onEnter?: (e: React.KeyboardEvent) => void;
  preventCloseOnBackdropClick?: boolean;
  shouldNotUseContext?: boolean;
  shouldRenderWhileLoading?: boolean;
  showBack?: boolean;
  storageId?: string;
  width?: string | number;
};

const ModalInner = React.forwardRef((props: ModalInnerProps, ref: any) => {
  const [isHidden, setIsHidden] = React.useState(false);
  const innerRef = React.useRef<HTMLDivElement>(null);

  const {
    action,
    hasArrow,
    cannotClose,
    className,
    enlagedHeight,
    enlagedWidth,
    hasBackdrop = true,
    isBorderless,
    isCentered = true,
    defaultIsEnlarged,
    isDraggable,
    isTopmost,
    defaultIsPinned,
    maxHeight,
    maxWidth,
    preventCloseOnBackdropClick,
    storageId,
    width,
    ...contentProps
  } = props;

  const ms = getState(props.storageId, width);

  const [state, setState] = React.useState<Partial<ModalState>>({
    ...ms,
    isEnlarged: ms.isEnlarged ?? defaultIsEnlarged,
    isPinned: ms.isPinned ?? defaultIsPinned
  });

  const onClose = React.useCallback(
    () => !props.cannotClose && props.onClose && props.onClose(),
    [props.cannotClose, props.onClose]
  );
  const onDragged = React.useCallback(
    (lt: LeftTop, isPin: boolean) => {
      saveState({ ...lt, isPinned: isPin }, props.storageId);

      if (isPin && !state.isPinned) {
        setState((x) => ({ ...x, isPinned: true }));
      }
    },
    [state.isPinned, props.storageId]
  );
  const onClick = React.useCallback((e: React.MouseEvent) => {
    // TODO: sort out
    // reason: in the diagram builder Delete modal is rendered INSIDE menus.
    // When clicking on an input, that has any element behind it (under the modal), it's impossible to focus the input.
    // My assumption is that the click event gets propagated, but it's not confirmed.
    e.stopPropagation();
  }, []);
  const onToggleEnlarged = React.useCallback(
    () => (
      setState({
        ...state,
        isEnlarged: !state.isEnlarged
      }),
      saveState({ isEnlarged: !state.isEnlarged }, props.storageId)
    ),
    [state, props.storageId]
  );
  const onTogglePinned = React.useCallback(
    () => (
      setState({
        ...state,
        isPinned: !state.isPinned
      }),
      saveState({ isPinned: !state.isPinned }, props.storageId)
    ),
    [state, props.storageId]
  );

  const modalContext = React.useContext(ModalContext);
  const appContext = React.useContext(AppContext);

  const id = React.useMemo(() => uuid(), []);

  React.useEffect(() => {
    if (props.shouldNotUseContext || !props.hasBackdrop) return;

    modalContext.register(id);
    return () => {
      modalContext.unregister(id);
    };
  }, [id, props.shouldNotUseContext]);

  React.useEffect(() => {
    modalContext.patch(id, {
      close: onClose,
      hide: () => setIsHidden(true),
      show: () => setIsHidden(false)
    });
  }, [id, onClose]);

  if (props.hasBackdrop) {
    useClickOutside("modal", {
      callback: onClose,
      cannotClose: preventCloseOnBackdropClick,
      ignoreRefs: [innerRef]
    });
  }

  const finalHeight = (state.isEnlarged ? enlagedHeight : maxHeight) ?? maxHeight;
  const finalWidth = (state.isEnlarged ? enlagedWidth : width) ?? width;

  return (
    <div
      className={classnames(
        "modal fade visible in",
        appContext.context === "admin" ? "cartly_modal" : "wsc_modal",
        className,
        {
          borderless: isBorderless,
          draggable: isDraggable,
          "no-backdrop": !hasBackdrop,
          narrow: props.isNarrow,
          hidden: isHidden,
          top_most: isTopmost
        }
      )}
      tabIndex={-1}
      onClick={onClick}
      role="dialog"
    >
      {isDraggable ? (
        <EnhancedDialog
          ref={innerRef}
          {...contentProps}
          action={action}
          allowOffScreen={AllowOffScreen.None}
          cannotClose={cannotClose}
          className={classnames("modal-dialog")}
          hasArrow={hasArrow}
          maxHeight={finalHeight}
          initialPosition={ms}
          isEnlarged={state.isEnlarged}
          isPinned={state.isPinned}
          onClose={onClose}
          onDragged={onDragged}
          onToggleEnlarged={onToggleEnlarged}
          onTogglePinned={onTogglePinned}
          shouldRenderWhileLoading={props.shouldRenderWhileLoading ?? false}
          style={{ maxWidth: finalWidth, width: finalWidth }}
        />
      ) : (
        <div
          ref={ref}
          className={classnames("modal-dialog", {
            "modal-dialog-centered": isCentered
          })}
          style={{
            maxWidth,
            width: finalWidth
          }}
        >
          {!!hasArrow && <Arrow />}

          <ContentWrap
            ref={innerRef}
            {...contentProps}
            action={action}
            cannotClose={cannotClose}
            isEnlarged={state.isEnlarged}
            isPinned={state.isPinned}
            maxHeight={finalHeight}
            onClose={onClose}
            onToggleEnlarged={onToggleEnlarged}
            onTogglePinned={onTogglePinned}
            shouldRenderWhileLoading={props.shouldRenderWhileLoading ?? false}
          />
        </div>
      )}
    </div>
  );
});

export type ModalProps = ModalInnerProps & {
  ref?: React.RefObject<HTMLDivElement | null>;
  containerSelector?: string;
  isVisible: boolean;
};

const Modal = (props: ModalProps) => {
  const { containerSelector, isVisible, ...rest } = props;

  if (!isVisible) return null;

  return (
    <Portal parentSelector={containerSelector}>
      <ModalInner {...rest} />
    </Portal>
  );
};
export default Modal;

Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;
